<div class="px-2 py-3">
  <!-- Header -->
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex items-start gap-3">
      <span class="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-400">
        <lucide-icon [name]="CalendarClock" class="h-5 w-5"></lucide-icon>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-gray-800 dark:text-white/90">{{ payments.meta.title }}</h2>
        <p class="text-sm text-gray-500 dark:text-gray-400">{{ payments.meta.subtitle }}</p>
      </div>
    </div>
    <span class="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600 dark:bg-white/5 dark:text-white/70">
      <lucide-icon [name]="Sparkles" class="h-3.5 w-3.5"></lucide-icon>
      Last run {{ payments.meta.lastRun }}
    </span>
  </div>

  <!-- Tabs -->
  <div class="mt-4 flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-800" role="tablist" aria-label="AI Events Assistant tabs">
    <button
      type="button"
      role="tab"
      class="-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
      [ngClass]="isTab('payments')
        ? 'border-brand-500 text-brand-600 dark:border-brand-400 dark:text-brand-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white/80'"
      [attr.aria-selected]="isTab('payments')"
      (click)="selectTab('payments')">
      Payments and risk
    </button>
    <button
      type="button"
      role="tab"
      class="-mb-px border-b-2 px-4 py-2.5 text-sm font-medium transition-colors"
      [ngClass]="isTab('upsell')
        ? 'border-brand-500 text-brand-600 dark:border-brand-400 dark:text-brand-400'
        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white/80'"
      [attr.aria-selected]="isTab('upsell')"
      (click)="selectTab('upsell')">
      Upsell
    </button>
  </div>

  <!-- Tab 1: Payments and risk -->
  @if (isTab('payments')) {
    <!-- Header summary -->
    <div class="mt-4 rounded-2xl border border-blue-200 bg-blue-50/50 p-4 dark:border-blue-500/30 dark:bg-blue-500/[0.06]">
      <div class="flex items-center gap-2 text-sm font-semibold text-blue-800 dark:text-blue-300">
        <lucide-icon [name]="Sparkles" class="h-4 w-4"></lucide-icon>
        Summary
      </div>
      <p class="mt-1.5 text-sm text-blue-900/80 dark:text-blue-100/80">{{ payments.meta.summary }}</p>
    </div>

    <!-- Board: ranked list + detail panel -->
    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-12">
      <!-- Ranked list -->
      <div class="space-y-2 lg:col-span-7">
        @for (event of payments.events; track event.id) {
          <button
            type="button"
            class="relative flex w-full items-start gap-3 rounded-2xl border bg-white p-4 text-left transition-colors dark:bg-white/[0.03]"
            [ngClass]="{
              'border-red-300 ring-1 ring-red-200 dark:border-red-500/40 dark:ring-red-500/20': event.band === 'high-risk',
              'border-gray-200 dark:border-gray-800': event.band !== 'high-risk',
              'outline outline-2 outline-brand-500': isEventSelected(event)
            }"
            [attr.data-event-id]="event.id"
            (click)="selectEvent(event)">
            @if (event.band === 'high-risk') {
              <span class="absolute inset-y-0 left-0 w-1 rounded-l-2xl bg-red-400 dark:bg-red-500"></span>
            }

            <!-- Rank -->
            <span class="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-sm font-semibold text-gray-700 dark:bg-white/5 dark:text-white/80">
              {{ event.rank }}
            </span>

            <div class="min-w-0 flex-1">
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-semibold text-gray-800 dark:text-white/90">{{ event.couple }}</span>
                <span class="rounded-full px-2 py-0.5 text-xs font-medium" [ngClass]="bandClasses(event.band)">
                  {{ event.bandLabel }}
                </span>
              </div>

              <div class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                {{ event.venue }} · {{ event.eventDate }}
              </div>

              <p class="mt-1.5 text-sm text-gray-600 dark:text-white/70">{{ event.reason }}</p>

              <!-- Chips: days overdue + weeks to the day -->
              <div class="mt-2 flex flex-wrap gap-1.5">
                <span class="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30">
                  {{ event.daysOverdue }} days overdue
                </span>
                <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-white/5 dark:text-white/70 dark:ring-white/10">
                  {{ event.weeksToDay }} weeks to the day
                </span>
              </div>
            </div>

            <!-- Amount overdue (headline) -->
            <div class="flex shrink-0 flex-col items-end gap-1.5">
              <div class="flex flex-col items-end rounded-xl bg-red-50 px-3 py-1.5 dark:bg-red-500/10">
                <span class="text-xl font-bold leading-none text-red-600 dark:text-red-400">{{ event.amountOverdue }}</span>
                <span class="mt-1 text-[10px] font-semibold uppercase tracking-wide text-red-700/70 dark:text-red-300/70">Overdue</span>
              </div>
            </div>
          </button>
        }
      </div>

      <!-- Detail panel -->
      <div class="lg:col-span-5">
        <div
          class="sticky top-4 rounded-2xl border bg-white p-5 dark:bg-white/[0.03]"
          [ngClass]="selectedEvent.band === 'high-risk'
            ? 'border-red-300 ring-1 ring-red-200 dark:border-red-500/40 dark:ring-red-500/20'
            : 'border-gray-200 dark:border-gray-800'">
          <!-- Panel header -->
          <div class="flex items-start justify-between gap-3">
            <div>
              <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ selectedEvent.couple }}</h3>
              <p class="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{{ selectedEvent.venue }} · {{ selectedEvent.eventDate }}</p>
            </div>
            <div class="flex shrink-0 flex-col items-end gap-1.5">
              <div class="flex flex-col items-end rounded-xl bg-red-50 px-3 py-1.5 dark:bg-red-500/10">
                <span class="text-lg font-bold leading-none text-red-600 dark:text-red-400">{{ selectedEvent.amountOverdue }}</span>
                <span class="text-[10px] font-semibold uppercase tracking-wide text-red-700/70 dark:text-red-300/70">Overdue</span>
              </div>
              <span class="rounded-full px-2.5 py-1 text-xs font-medium" [ngClass]="bandClasses(selectedEvent.band)">
                {{ selectedEvent.bandLabel }}
              </span>
            </div>
          </div>

          <!-- Overdue / weeks chips -->
          <div class="mt-3 flex flex-wrap gap-1.5">
            <span class="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-red-200 dark:bg-red-500/15 dark:text-red-300 dark:ring-red-500/30">
              {{ selectedEvent.daysOverdue }} days overdue
            </span>
            <span class="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 ring-1 ring-gray-200 dark:bg-white/5 dark:text-white/70 dark:ring-white/10">
              {{ selectedEvent.weeksToDay }} weeks to the day
            </span>
          </div>

          <!-- Payment breakdown -->
          <div class="mt-4">
            <h4 class="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Payment breakdown</h4>
            <dl class="mt-2 divide-y divide-gray-100 rounded-xl border border-gray-200 dark:divide-gray-800 dark:border-gray-800">
              @for (row of selectedEvent.payment; track row[0]) {
                <div class="flex items-center justify-between gap-3 px-3 py-2">
                  <dt class="text-xs text-gray-500 dark:text-gray-400">{{ row[0] }}</dt>
                  <dd class="text-sm font-medium text-gray-800 dark:text-white/90">{{ row[1] }}</dd>
                </div>
              }
            </dl>
          </div>

          <!-- Suggested balance reminder -->
          <div class="mt-4">
            <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              <lucide-icon [name]="Mail" class="h-4 w-4"></lucide-icon>
              Suggested balance reminder
            </div>
            <div class="mt-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <div class="text-sm font-medium text-gray-800 dark:text-white/90">{{ selectedEvent.reminderSubject }}</div>
              <p class="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-white/70">{{ selectedEvent.reminderBody }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  }

  <!-- Tab 2: Upsell (illustrative placeholder) -->
  @if (isTab('upsell')) {
    <!-- Placeholder banner -->
    <div class="mt-4 rounded-2xl border border-amber-200 bg-amber-50/60 p-4 dark:border-amber-500/30 dark:bg-amber-500/[0.06]">
      <div class="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-300">
        <lucide-icon [name]="TrendingUp" class="h-4 w-4"></lucide-icon>
        Illustrative examples only
      </div>
      <p class="mt-1.5 text-sm text-amber-900/80 dark:text-amber-100/80">{{ upsell.note }}</p>
    </div>

    <!-- Two made-up examples -->
    <div class="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
      @for (example of upsell.examples; track example.id) {
        <div class="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
          <h3 class="text-base font-semibold text-gray-800 dark:text-white/90">{{ example.couple }}</h3>

          <dl class="mt-3 space-y-2">
            <div>
              <dt class="text-xs text-gray-500 dark:text-gray-400">Currently</dt>
              <dd class="text-sm text-gray-700 dark:text-white/80">{{ example.current }}</dd>
            </div>
            <div>
              <dt class="text-xs text-gray-500 dark:text-gray-400">Budget</dt>
              <dd class="text-sm font-semibold text-gray-800 dark:text-white/90">{{ example.budget }}</dd>
            </div>
            <div>
              <dt class="text-xs text-gray-500 dark:text-gray-400">Assistant suggests</dt>
              <dd class="text-sm text-gray-700 dark:text-white/80">{{ example.suggestion }}</dd>
            </div>
          </dl>

          <p class="mt-3 text-sm leading-relaxed text-gray-600 dark:text-white/70">{{ example.rationale }}</p>

          <!-- Example email -->
          <div class="mt-4">
            <div class="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              <lucide-icon [name]="Mail" class="h-4 w-4"></lucide-icon>
              Example email
            </div>
            <div class="mt-2 rounded-xl border border-gray-200 bg-gray-50/60 p-3 dark:border-gray-800 dark:bg-white/[0.02]">
              <div class="text-sm font-medium text-gray-800 dark:text-white/90">{{ example.exampleSubject }}</div>
              <p class="mt-2 whitespace-pre-line text-sm leading-relaxed text-gray-600 dark:text-white/70">{{ example.exampleEmail }}</p>
            </div>
          </div>
        </div>
      }
    </div>
  }
</div>
